// =============================================
// Parent Contacts Management - ניהול אנשי קשר
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  UserPlus,
  X,
  Phone,
  Bell,
  BellOff,
  Trash2,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Crown,
  User,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { parentContactsHelpTexts } from '../../constants/helpTexts';
import { parentColors } from '../../styles/colors';

// Helper to get auth token
const getAuthToken = (): string | null => {
  return useAuthStore.getState().token;
};

const colors = {
  ...parentColors,
  background: '#f9fafb',
};

interface Child {
  publicId: string;
  firstName: string;
  lastName: string;
  kindergartenName: string;
  photoUrl?: string;
  isPayingParent?: boolean;
}

interface Contact {
  publicId: string;
  firstName: string;
  lastName: string;
  phone: string;
  relationType: string;
  isPayingParent: boolean;
  isCurrentUser?: boolean; // האם זה המשתמש המחובר
  alertOrder: number | null; // Bitmask: 1=first, 2=second, 3=both
  receiveAlerts: boolean;
  receiveFirstAlert?: boolean;
  receiveSecondAlert?: boolean;
  canMarkAttendance: boolean;
  status: string;
}

interface Invitation {
  id: number;
  invitedPhone: string;
  invitedName?: string;
  relationType: string;
  status: string;
  expiresAt: string;
  children: { publicId: string; firstName: string; lastName: string }[];
}

interface PendingTransfer {
  id: number;
  childPublicId: string;
  childName: string;
  fromParentName: string;
  status: string;
  expiresAt: string;
  existingContacts: Contact[];
}


export default function ParentContacts() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState<PendingTransfer | null>(null);
  const [expandedChild, setExpandedChild] = useState<string | null>(null);
  const [sendingInvitation, setSendingInvitation] = useState(false);

  // Invite form state
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRelation, setInviteRelation] = useState('Parent');
  const [selectedChildrenForInvite, setSelectedChildrenForInvite] = useState<string[]>([]);

  // For backward compatibility, compute full name from first + last
  const inviteName = `${inviteFirstName} ${inviteLastName}`.trim();

  // Message modal state
  const [messageModal, setMessageModal] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  } | null>(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  } | null>(null);

  const showMessage = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setMessageModal({ show: true, type, title, message });
  };

  const closeMessage = () => {
    setMessageModal(null);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void, confirmText = 'אישור', cancelText = 'ביטול') => {
    setConfirmModal({ show: true, title, message, onConfirm, confirmText, cancelText });
  };

  const closeConfirm = () => {
    setConfirmModal(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchContacts(selectedChild.publicId);
    }
  }, [selectedChild]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Try to fetch children from API first
      let childrenFromApi: Child[] = [];
      try {
        const childrenRes = await fetch(`${API_BASE_URL}/children`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const childrenData = await childrenRes.json();

        if (childrenRes.status === 401) {
          showMessage('error', 'לא מחובר', 'נא להתחבר מחדש');
          navigate('/parent/login');
          return;
        }

        if (childrenData.success && childrenData.data?.length > 0) {
          console.log('Children from API:', childrenData.data);
          // Map API response to local Child interface
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          childrenFromApi = childrenData.data.map((c: any) => ({
            publicId: c.childPublicId,
            firstName: c.childFirstName,
            lastName: c.childLastName,
            photoUrl: c.childPhotoUrl,
            kindergartenName: c.kindergartenName,
            isPayingParent: c.isPayingParent === true,
          }));
          console.log('Mapped children:', childrenFromApi);
        }
      } catch (apiError) {
        console.log('API not available, using localStorage');
      }

      // If no children from API, try localStorage (dev mode fallback)
      if (childrenFromApi.length === 0) {
        try {
          const storedChildren = localStorage.getItem('kipikid-children');
          if (storedChildren) {
            const parsed = JSON.parse(storedChildren);
            // Convert localStorage format to API format
            // Note: localStorage data doesn't have isPayingParent info, so show all children
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            childrenFromApi = parsed.map((c: any) => ({
              // Use publicId if it's a valid GUID, otherwise use the id if it looks like a GUID
              publicId: c.publicId || c.id?.toString() || `local-${Date.now()}`,
              firstName: c.firstName,
              lastName: c.lastName,
              photoUrl: c.photoUrl,
              kindergartenName: c.kindergartenName,
              // If isPayingParent is stored, use it; otherwise default to true for localStorage fallback
              isPayingParent: c.isPayingParent ?? true,
            }));
            console.log('Children loaded from localStorage:', childrenFromApi);
          }
        } catch (e) {
          console.error('Error loading from localStorage:', e);
        }
      }

      if (childrenFromApi.length > 0) {
        setChildren(childrenFromApi);
        // Select the first child where user is paying parent
        const payingParentChildren = childrenFromApi.filter(c => c.isPayingParent);
        setSelectedChild(payingParentChildren[0] || childrenFromApi[0]);
      }

      // Fetch sent invitations
      try {
        const invitationsRes = await fetch(`${API_BASE_URL}/invitations/sent`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const invitationsData = await invitationsRes.json();
        if (invitationsData.success) {
          setInvitations(invitationsData.data);
        }
      } catch {
        // No mock invitations
      }

      // Fetch pending transfers
      try {
        const transfersRes = await fetch(`${API_BASE_URL}/transfers/pending`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const transfersData = await transfersRes.json();
        if (transfersData.success) {
          setPendingTransfers(transfersData.data);
        }
      } catch {
        // No mock transfers
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (childPublicId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/children/${childPublicId}/contacts`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setContacts(data.data || []);
      } else {
        console.error('Failed to fetch contacts:', data.message);
        setContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Israeli phone format: 05X-XXX-XXXX or 05XXXXXXXX
    const cleanPhone = phone.replace(/[-\s]/g, '');
    return /^05\d{8}$/.test(cleanPhone);
  };

  const sendInvitation = async () => {
    if (!invitePhone || selectedChildrenForInvite.length === 0) {
      showMessage('warning', 'שדות חסרים', 'נא למלא מספר טלפון ולבחור לפחות ילד אחד');
      return;
    }

    if (!validatePhoneNumber(invitePhone)) {
      showMessage('warning', 'מספר טלפון לא תקין', 'יש להזין מספר נייד ישראלי (05X-XXX-XXXX)');
      return;
    }

    setSendingInvitation(true);

    // Validate that all selected children have valid GUID publicIds
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const validChildrenIds = selectedChildrenForInvite.filter(id => guidRegex.test(id));

    if (validChildrenIds.length === 0) {
      showMessage('error', 'שגיאה', 'לא ניתן לשלוח הזמנה - נתוני הילדים לא תקינים. נסה לרענן את הדף.');
      setSendingInvitation(false);
      return;
    }

    if (validChildrenIds.length !== selectedChildrenForInvite.length) {
      console.warn('Some children have invalid publicIds:', selectedChildrenForInvite.filter(id => !guidRegex.test(id)));
    }

    console.log('Sending invitation with:', {
      Phone: invitePhone,
      FirstName: inviteFirstName,
      LastName: inviteLastName,
      Email: inviteEmail,
      RelationTypeStr: inviteRelation,
      ChildrenPublicIds: validChildrenIds,
    });

    try {
      const res = await fetch(`${API_BASE_URL}/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          Phone: invitePhone,
          FirstName: inviteFirstName || undefined,
          LastName: inviteLastName || undefined,
          Email: inviteEmail || undefined,
          RelationTypeStr: inviteRelation,
          ChildrenPublicIds: validChildrenIds,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        console.error('Invitation failed:', res.status, errorData);
        const errorMessage = errorData?.message ||
                            (res.status === 401 ? 'לא מחובר - נא להתחבר מחדש' :
                             res.status === 429 ? 'נשלחו יותר מדי הזמנות. נסה שוב מאוחר יותר' :
                             res.status >= 500 ? 'שגיאת שרת. נסה שוב מאוחר יותר' :
                             'שגיאה בשליחת ההזמנה');
        showMessage('error', 'שגיאה', errorMessage);
        return;
      }

      const data = await res.json();
      if (data.success) {
        showMessage('success', 'הזמנה נשלחה', 'ההזמנה נשלחה בהצלחה!');
        setShowInviteModal(false);
        setInvitePhone('');
        setInviteFirstName('');
        setInviteLastName('');
        setInviteEmail('');
        setSelectedChildrenForInvite([]);
        fetchData();
      } else {
        showMessage('error', 'שגיאה', data.message || 'שגיאה בשליחת ההזמנה');
        console.error('Invitation error:', data.message);
      }
    } catch (error) {
      console.error('Network error sending invitation:', error);
      showMessage('error', 'שגיאת רשת', 'בדוק את החיבור לאינטרנט ונסה שוב');
    } finally {
      setSendingInvitation(false);
    }
  };

  const removeContact = async (contactPublicId: string) => {
    if (!selectedChild) return;

    showConfirm(
      'הסרת איש קשר',
      'האם אתה בטוח שברצונך להסיר את איש הקשר?',
      async () => {
        closeConfirm();
        try {
          const res = await fetch(
            `/api/children/${selectedChild.publicId}/contacts/${contactPublicId}`,
            {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${getAuthToken()}` },
            }
          );

          const data = await res.json();
          if (data.success) {
            fetchContacts(selectedChild.publicId);
          } else {
            showMessage('error', 'שגיאה', data.message || 'שגיאה בהסרת איש הקשר');
          }
        } catch {
          showMessage('error', 'שגיאה', 'שגיאה בהסרת איש הקשר');
        }
      },
      'הסר',
      'ביטול'
    );
  };

  // Toggle first or second alert for a contact
  const toggleAlert = async (contactPublicId: string, alertType: 'first' | 'second', enable: boolean) => {
    if (!selectedChild) return;

    const contact = contacts.find(c => c.publicId === contactPublicId);
    if (!contact) return;

    // Get current alert state from bitmask or individual flags
    let currentFirst = contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0;
    let currentSecond = contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0;

    // Update the requested alert
    if (alertType === 'first') {
      currentFirst = enable;
      // If enabling first alert, automatically enable second (user can disable if needed)
      if (enable && !currentSecond) {
        currentSecond = true;
      }
    } else {
      currentSecond = enable;
    }

    // Validation: must have at least one contact with first alert enabled
    if (alertType === 'first' && !enable) {
      const otherFirstAlertContacts = contacts.filter(c =>
        c.publicId !== contactPublicId &&
        (c.receiveFirstAlert ?? ((c.alertOrder ?? 0) & 1) !== 0)
      );
      if (otherFirstAlertContacts.length === 0) {
        showMessage('warning', 'לא ניתן להסיר', 'חייב להיות לפחות איש קשר אחד שמקבל הודעה ראשונה');
        return;
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/children/${selectedChild.publicId}/alert-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          ContactPublicId: contactPublicId,
          ReceiveFirstAlert: currentFirst,
          ReceiveSecondAlert: currentSecond,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        showMessage('error', 'שגיאה', data.message || 'שגיאה בעדכון הגדרות ההתראה');
      }

      fetchContacts(selectedChild.publicId);
    } catch (error) {
      console.error('Error updating alert settings:', error);
      showMessage('error', 'שגיאה', 'שגיאת רשת בעדכון הגדרות');
    }
  };

  // Legacy function for backward compatibility
  const updateAlertSettings = async (contactPublicId: string, alertOrder: number | null) => {
    if (!selectedChild) return;

    try {
      await fetch(`${API_BASE_URL}/children/${selectedChild.publicId}/alert-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          ContactPublicId: contactPublicId,
          AlertOrder: alertOrder,
          ReceiveAlerts: alertOrder !== null && alertOrder > 0,
        }),
      });

      fetchContacts(selectedChild.publicId);
    } catch (error) {
      console.error('Error updating alert settings:', error);
    }
  };

  const respondToTransfer = async (transferId: number, accept: boolean) => {
    try {
      const res = await fetch(`${API_BASE_URL}/transfers/${transferId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ Accept: accept }),
      });

      const data = await res.json();
      if (data.success) {
        setShowTransferModal(null);
        fetchData();
        if (accept) {
          showMessage('success', 'הורה משלם', 'קיבלת את תפקיד ההורה המשלם. עכשיו תוכל לאשר את אנשי הקשר.');
        }
      } else {
        showMessage('error', 'שגיאה', data.message || 'שגיאה בתגובה לבקשה');
      }
    } catch {
      showMessage('error', 'שגיאה', 'שגיאה בתגובה לבקשה');
    }
  };

  const cancelInvitation = async (invitationId: number) => {
    showConfirm(
      'ביטול הזמנה',
      'האם לבטל את ההזמנה?',
      async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API_BASE_URL}/invitations/${invitationId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          });

          const data = await res.json();
          if (data.success) {
            fetchData();
            showMessage('success', 'הזמנה בוטלה', 'ההזמנה בוטלה בהצלחה');
          }
        } catch (error) {
          console.error('Error cancelling invitation:', error);
          showMessage('error', 'שגיאה', 'שגיאה בביטול ההזמנה');
        }
      },
      'בטל הזמנה',
      'השאר'
    );
  };

  const getRelationLabel = (type: string) => {
    const labels: Record<string, string> = {
      PayingParent: 'הורה משלם',
      Parent: 'הורה',
      Contact: 'איש קשר',
      Grandparent: 'סב/סבתא',
      Nanny: 'מטפלת',
      Other: 'אחר',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending':
        return (
          <span style={{
            backgroundColor: colors.warningLight,
            color: colors.warning,
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Clock size={12} />
            ממתין
          </span>
        );
      case 'Accepted':
        return (
          <span style={{
            backgroundColor: colors.successLight,
            color: colors.success,
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <CheckCircle2 size={12} />
            אושר
          </span>
        );
      case 'Declined':
        return (
          <span style={{
            backgroundColor: colors.dangerLight,
            color: colors.danger,
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <XCircle size={12} />
            נדחה
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
      }}>
        <Loader2 size={40} color={colors.primary} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Back Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/parent/dashboard')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          marginBottom: '16px',
          backgroundColor: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          cursor: 'pointer',
          color: colors.text,
          fontSize: '14px',
        }}
      >
        <ArrowRight size={18} />
        חזרה למסך הבית
      </motion.button>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '8px' }}>
          <Users size={28} style={{ display: 'inline', marginLeft: '8px', verticalAlign: 'middle' }} />
          ניהול אנשי קשר
        </h1>
        <p style={{ color: colors.textLight, fontSize: '14px' }}>
          הוסף, ערוך והסר הורים ואנשי קשר עבור הילדים שלך
        </p>
      </div>

      {/* Pending Transfers Alert */}
      {pendingTransfers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            backgroundColor: colors.warningLight,
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px',
            border: `2px solid ${colors.warning}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <AlertTriangle size={24} color={colors.warning} />
            <div style={{ fontWeight: '600', color: colors.text }}>
              יש בקשות להפוך להורה משלם
            </div>
          </div>
          {pendingTransfers.map((transfer) => (
            <div
              key={transfer.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: '500' }}>{transfer.childName}</div>
                <div style={{ fontSize: '13px', color: colors.textLight }}>
                  מ-{transfer.fromParentName}
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowTransferModal(transfer)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                צפה בפרטים
              </motion.button>
            </div>
          ))}
        </motion.div>
      )}

      {/* No paying parent children - show message */}
      {children.filter(c => c.isPayingParent).length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <Users size={48} strokeWidth={1.5} style={{ marginBottom: '12px', opacity: 0.5, color: colors.textLight }} />
          <h3 style={{ fontSize: '18px', color: colors.text, marginBottom: '8px' }}>אין הרשאה לניהול אנשי קשר</h3>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            רק הורה משלם יכול לנהל אנשי קשר של הילד.
          </p>
        </div>
      )}

      {/* Child Selection - show only children where user is paying parent */}
      {children.filter(c => c.isPayingParent).length > 1 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px', display: 'block' }}>
            בחר ילד
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {children.filter(c => c.isPayingParent).map((child) => (
              <motion.button
                key={child.publicId}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedChild(child)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: `2px solid ${selectedChild?.publicId === child.publicId ? colors.primary : colors.border}`,
                  backgroundColor: selectedChild?.publicId === child.publicId ? colors.primaryLight : 'white',
                  color: selectedChild?.publicId === child.publicId ? colors.primary : colors.text,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {child.firstName} {child.lastName}
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Contacts List */}
      {selectedChild && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          marginBottom: '20px',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text }}>
              אנשי קשר של {selectedChild.firstName}
            </h2>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSelectedChildrenForInvite([selectedChild.publicId]);
                setShowInviteModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              <UserPlus size={18} />
              הזמן איש קשר
            </motion.button>
          </div>

          {contacts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: colors.textLight,
            }}>
              <Users size={48} strokeWidth={1.5} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>אין אנשי קשר</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {contacts.map((contact) => {
                // המשתמש הנוכחי מוצג בכתום, שאר אנשי הקשר בירוק
                const isCurrentUser = contact.isCurrentUser === true;
                const cardBorderColor = isCurrentUser ? colors.primary : colors.secondary;
                const cardBgColor = isCurrentUser ? colors.primaryLight : colors.secondaryLight;
                const iconBgColor = isCurrentUser ? colors.primary : colors.secondary;

                return (
                <div
                  key={contact.publicId}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${cardBorderColor}`,
                    backgroundColor: cardBgColor,
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: iconBgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {isCurrentUser ? (
                          <Crown size={24} color="white" />
                        ) : (
                          <User size={24} color="white" />
                        )}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: colors.text,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          {contact.firstName} {contact.lastName}
                          {isCurrentUser && (
                            <span style={{
                              fontSize: '11px',
                              backgroundColor: colors.primary,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '6px',
                            }}>
                              הורה משלם
                            </span>
                          )}
                          {/* תווית "הורה משלם" לאנשי קשר שהם הורים משלמים לילד אחר */}
                          {!isCurrentUser && contact.isPayingParent && (
                            <span style={{
                              fontSize: '11px',
                              backgroundColor: colors.primary,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '6px',
                            }}>
                              הורה משלם
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: colors.textLight }}>
                          {getRelationLabel(contact.relationType)} • {contact.phone}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Alert Toggle Checkboxes */}
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {/* First Alert Checkbox */}
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            const hasFirst = contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0;
                            toggleAlert(contact.publicId, 'first', !hasFirst);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: `2px solid ${(contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) ? colors.primary : colors.border}`,
                            backgroundColor: (contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) ? colors.primaryLight : 'white',
                            color: (contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) ? colors.primary : colors.textLight,
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '500',
                          }}
                          title="התראה ראשונה"
                        >
                          <span style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '3px',
                            border: `2px solid ${(contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) ? colors.primary : colors.border}`,
                            backgroundColor: (contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) ? colors.primary : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '10px',
                          }}>
                            {(contact.receiveFirstAlert ?? ((contact.alertOrder ?? 0) & 1) !== 0) && '✓'}
                          </span>
                          1
                        </motion.button>

                        {/* Second Alert Checkbox */}
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            const hasSecond = contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0;
                            toggleAlert(contact.publicId, 'second', !hasSecond);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: `2px solid ${(contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) ? colors.secondary : colors.border}`,
                            backgroundColor: (contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) ? colors.secondaryLight : 'white',
                            color: (contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) ? colors.secondary : colors.textLight,
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '500',
                          }}
                          title="התראה שנייה"
                        >
                          <span style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '3px',
                            border: `2px solid ${(contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) ? colors.secondary : colors.border}`,
                            backgroundColor: (contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) ? colors.secondary : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '10px',
                          }}>
                            {(contact.receiveSecondAlert ?? ((contact.alertOrder ?? 0) & 2) !== 0) && '✓'}
                          </span>
                          2
                        </motion.button>
                      </div>

                      {/* Remove Button (only for non-current user contacts) */}
                      {!isCurrentUser && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => removeContact(contact.publicId)}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: colors.dangerLight,
                            color: colors.danger,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          title="הסר"
                        >
                          <Trash2 size={16} />
                        </motion.button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.filter(i => i.status === 'Pending').length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
            <Send size={20} style={{ display: 'inline', marginLeft: '8px' }} />
            הזמנות ממתינות
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {invitations.filter(i => i.status === 'Pending').map((invitation) => (
              <div
                key={invitation.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  backgroundColor: colors.warningLight,
                }}
              >
                <div>
                  <div style={{ fontWeight: '500', color: colors.text }}>
                    {invitation.invitedName || invitation.invitedPhone}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight }}>
                    {getRelationLabel(invitation.relationType)} •
                    עבור: {invitation.children.map(c => c.firstName).join(', ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {getStatusBadge(invitation.status)}
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => cancelInvitation(invitation.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'white',
                      color: colors.danger,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    title="בטל הזמנה"
                  >
                    <X size={14} />
                  </motion.button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowInviteModal(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '400px',
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text }}>
                  הזמן הורה/איש קשר חדש
                </h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                  }}
                >
                  <X size={20} color={colors.textLight} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <FormLabel text="מספר טלפון" required helpText={parentContactsHelpTexts.contactPhone.tooltip} style={{ color: colors.text }} />
                  <input
                    type="tel"
                    placeholder="05X-XXX-XXXX"
                    value={invitePhone}
                    onChange={(e) => setInvitePhone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '10px',
                      fontSize: '15px',
                      direction: 'ltr',
                      textAlign: 'right',
                      boxSizing: 'border-box' as const,
                      outline: 'none',
                    }}
                  />
                  <HelperText text={parentContactsHelpTexts.contactPhone.helper!} />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: colors.text, display: 'block', marginBottom: '6px' }}>
                      שם פרטי
                    </label>
                    <input
                      type="text"
                      placeholder="שם פרטי"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '10px',
                        fontSize: '15px',
                        direction: 'rtl',
                        boxSizing: 'border-box' as const,
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: colors.text, display: 'block', marginBottom: '6px' }}>
                      שם משפחה
                    </label>
                    <input
                      type="text"
                      placeholder="שם משפחה"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '10px',
                        fontSize: '15px',
                        direction: 'rtl',
                        boxSizing: 'border-box' as const,
                        outline: 'none',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <FormLabel text="אימייל" helpText={parentContactsHelpTexts.contactEmail.tooltip} style={{ color: colors.text }} />
                  <input
                    type="email"
                    placeholder="example@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '10px',
                      fontSize: '15px',
                      direction: 'ltr',
                      textAlign: 'left',
                      boxSizing: 'border-box' as const,
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <FormLabel text="סוג קשר" helpText={parentContactsHelpTexts.relation.tooltip} style={{ color: colors.text }} />
                  <select
                    value={inviteRelation}
                    onChange={(e) => setInviteRelation(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '10px',
                      fontSize: '15px',
                      backgroundColor: 'white',
                      boxSizing: 'border-box' as const,
                      outline: 'none',
                    }}
                  >
                    <option value="Parent">הורה</option>
                    <option value="Grandparent">סב/סבתא</option>
                    <option value="Nanny">מטפלת</option>
                    <option value="Contact">איש קשר</option>
                    <option value="Other">אחר</option>
                  </select>
                </div>

                {/* Show only children where user is paying parent */}
                {children.filter(c => c.isPayingParent).length > 0 && (
                  <div>
                    <FormLabel text="עבור אילו ילדים?" helpText={parentContactsHelpTexts.childrenSelection.tooltip} style={{ color: colors.text }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {children.filter(c => c.isPayingParent).map((child) => (
                        <label
                          key={child.publicId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: `2px solid ${selectedChildrenForInvite.includes(child.publicId) ? colors.primary : colors.border}`,
                            backgroundColor: selectedChildrenForInvite.includes(child.publicId) ? colors.primaryLight : 'white',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedChildrenForInvite.includes(child.publicId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedChildrenForInvite([...selectedChildrenForInvite, child.publicId]);
                              } else {
                                setSelectedChildrenForInvite(selectedChildrenForInvite.filter(id => id !== child.publicId));
                              }
                            }}
                            style={{ width: '18px', height: '18px', accentColor: colors.primary }}
                          />
                          <span style={{ fontSize: '14px', color: colors.text }}>
                            {child.firstName} {child.lastName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <motion.button
                  whileTap={{ scale: sendingInvitation ? 1 : 0.98 }}
                  onClick={sendInvitation}
                  disabled={sendingInvitation}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: sendingInvitation ? colors.textLight : colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: sendingInvitation ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: sendingInvitation ? 0.7 : 1,
                  }}
                >
                  {sendingInvitation ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      שולח...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      שלח הזמנה
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transfer Modal */}
      <AnimatePresence>
        {showTransferModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowTransferModal(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '450px',
              }}
            >
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
                בקשה להפוך להורה משלם
              </h3>

              <div style={{
                backgroundColor: colors.warningLight,
                padding: '16px',
                borderRadius: '12px',
                marginBottom: '16px',
              }}>
                <p style={{ fontSize: '14px', color: colors.text, marginBottom: '8px' }}>
                  <strong>{showTransferModal.fromParentName}</strong> מבקש להעביר אליך את תפקיד ההורה המשלם
                  עבור <strong>{showTransferModal.childName}</strong>.
                </p>
                <p style={{ fontSize: '13px', color: colors.textLight }}>
                  אם תקבל, תוכל לנהל את אנשי הקשר של הילד ותהיה אחראי לתשלום.
                </p>
              </div>

              {showTransferModal.existingContacts.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>
                    אנשי קשר קיימים:
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {showTransferModal.existingContacts.map((contact) => (
                      <div
                        key={contact.publicId}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: colors.background,
                          borderRadius: '8px',
                          fontSize: '13px',
                        }}
                      >
                        {contact.firstName} {contact.lastName} ({getRelationLabel(contact.relationType)})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => respondToTransfer(showTransferModal.id, false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: colors.dangerLight,
                    color: colors.danger,
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  דחה
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => respondToTransfer(showTransferModal.id, true)}
                  style={{
                    flex: 2,
                    padding: '12px',
                    backgroundColor: colors.success,
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <Crown size={18} />
                  קבל תפקיד
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message Modal */}
      <AnimatePresence>
        {messageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeMessage();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '320px',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: messageModal.type === 'success' ? colors.successLight :
                                messageModal.type === 'error' ? colors.dangerLight :
                                messageModal.type === 'warning' ? colors.warningLight :
                                colors.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                {messageModal.type === 'success' && <CheckCircle2 size={28} color={colors.success} />}
                {messageModal.type === 'error' && <XCircle size={28} color={colors.danger} />}
                {messageModal.type === 'warning' && <AlertTriangle size={28} color={colors.warning} />}
                {messageModal.type === 'info' && <Bell size={28} color={colors.primary} />}
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
                {messageModal.title}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0', lineHeight: '1.5' }}>
                {messageModal.message}
              </p>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={closeMessage}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  backgroundColor: messageModal.type === 'success' ? colors.success :
                                  messageModal.type === 'error' ? colors.danger :
                                  messageModal.type === 'warning' ? colors.warning :
                                  colors.primary,
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                אישור
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Modal */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeConfirm();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '320px',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: colors.warningLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <AlertTriangle size={28} color={colors.warning} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
                {confirmModal.title}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0', lineHeight: '1.5' }}>
                {confirmModal.message}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <motion.button
                  autoFocus
                  whileTap={{ scale: 0.98 }}
                  onClick={closeConfirm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: colors.background,
                    color: colors.text,
                    fontSize: '15px',
                    fontWeight: '600',
                    border: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                  }}
                >
                  {confirmModal.cancelText}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={confirmModal.onConfirm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: colors.danger,
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {confirmModal.confirmText}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
